import type { ProcessedEntry, InvoiceLine } from '../types/financialReport';

function escape(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes(';')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function isoDate(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
}

function number(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return '';
  return value.toFixed(2);
}

const HEADERS = [
  'Year',
  'Work Code',
  'Campaign Ref',
  'Campaign Mode',
  'Client',
  'Brand',
  'Campaign Status',
  'Agent',
  'Campaign Fee',
  'Commission %',
  'Commission Amount',
  'Campaign Budget',
  'Net Influencer Cost',
  'Net Agency Margin',
  'Influencers',
  'Influencer Count',
  'Campaign Client Billed',
  'Campaign Client Received',
  'Campaign Client Balance',
  'Campaign Supplier Billed',
  'Campaign Supplier Paid',
  'Campaign Supplier Balance',
  'Invoice Side',
  'Counterparty',
  'Invoice Number',
  'Client Invoice Number',
  'Purchase Order',
  'Status (EN)',
  'Status (PT)',
  'Category',
  'Issued Date',
  'Due Date',
  'Payment Date',
  'Invoice Amount',
  'Amount Paid',
  'Balance',
  'Days To Payment',
  'Days Overdue',
  'Campaign Notes',
  'Campaign Created At',
  'Campaign Updated At',
];

function campaignPrefix(entry: ProcessedEntry): (string | number)[] {
  return [
    entry.year,
    entry.work_code,
    entry.campaign_ref || '',
    entry.campaign_mode,
    entry.client,
    entry.brand,
    entry.campaign_status,
    entry.agent,
    number(entry.fee),
    entry.commission_pct.toFixed(2) + '%',
    number(entry.commission_amount),
    entry.campaign_budget > 0 ? number(entry.campaign_budget) : '',
    number(entry.net_influencer_cost),
    number(entry.net_agency_margin),
    entry.influencers,
    entry.influencer_count,
    number(entry.client_billed),
    number(entry.client_received),
    number(entry.client_balance_due),
    number(entry.supplier_billed),
    number(entry.supplier_paid),
    number(entry.supplier_balance_due),
  ];
}

function invoiceColumns(line: InvoiceLine | null, entry: ProcessedEntry): (string | number)[] {
  if (!line) {
    return [
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      entry.notes,
      isoDate(entry.created_at),
      isoDate(entry.updated_at),
    ];
  }

  const counterparty = line.side === 'client' ? entry.client : line.label || entry.influencers.split(';')[0]?.trim() || '';
  return [
    line.side === 'client' ? 'Client (Mohit -> Client)' : 'Supplier (Creator -> Mohit)',
    counterparty,
    line.invoice_number,
    line.client_invoice_number,
    line.purchase_order,
    line.status_en,
    line.status_pt,
    line.category,
    isoDate(line.issued_date),
    isoDate(line.due_date),
    isoDate(line.payment_date),
    number(line.amount),
    number(line.amount_paid),
    number(line.balance),
    line.days_to_payment === null ? '' : String(line.days_to_payment),
    line.days_overdue > 0 ? String(line.days_overdue) : '',
    entry.notes,
    isoDate(entry.created_at),
    isoDate(entry.updated_at),
  ];
}

export function generateFinancialCSV(entries: ProcessedEntry[]): string {
  const rows: (string | number)[][] = [HEADERS];

  for (const entry of entries) {
    const lines = [...entry.client_invoices, ...entry.supplier_invoices];
    if (lines.length === 0) {
      rows.push([...campaignPrefix(entry), ...invoiceColumns(null, entry)]);
      continue;
    }
    for (const line of lines) {
      rows.push([...campaignPrefix(entry), ...invoiceColumns(line, entry)]);
    }
  }

  const BOM = '\uFEFF';
  return BOM + rows.map(r => r.map(escape).join(',')).join('\n');
}

export function downloadFinancialCSV(entries: ProcessedEntry[], yearLabel: string): void {
  const csv = generateFinancialCSV(entries);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const timestamp = new Date().toISOString().split('T')[0];
  link.href = url;
  link.download = `financial_report_${yearLabel}_${timestamp}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
